import { getEnvironment, isEnvironment } from './environment';
import cloneDeep from 'lodash/cloneDeep';
import { getRegionShortName, getRegionSuffix } from './getRegion';
import merge from 'lodash/merge';
import { Environments } from '../types/environments.types';
export const generateStandardUrl = (subDomain, env) => {
    const region = getRegionSuffix();
    return `https://${subDomain}.info360${region ? '-' + region : ''}-${env === 'prod' ? 'prd' : env}.com/${env}`;
};
export const removePathPartFromUrl = (url) => {
    const urlParts = url.split('/');
    urlParts.pop();
    return urlParts.join('/');
};
export const generateNotificationHubAppsyncUrl = (subdomain) => {
    const region = getRegionSuffix();
    return `https://${subdomain}.info360${region ? '-' + region : ''}-prd.com/graphql`;
};
const getPlatformEnvironmentUrlTag = (env) => {
    switch (env) {
        case Environments.LOCAL:
            return 'c';
        case Environments.DEV:
            return 'c';
        case Environments.QA:
            return 's';
        case Environments.STAGE:
            return 's';
        case Environments.PROD:
            return 'p';
    }
};
export const generatePlatformUrl = (subdomain, env) => {
    const regionShortName = getRegionShortName();
    const envTag = getPlatformEnvironmentUrlTag(env);
    //example: https://saas-notification-hub-prd-prd-appsync.saas-notification-hub.wlc360p-p-uw2.autodesk.com
    return `https://${subdomain}.wlc360p-${envTag}-${regionShortName}.autodesk.com`;
};
export const generateDeviantUrl = (url) => {
    const regionRegex = /\|region\|/g;
    const region = getRegionSuffix();
    return region
        ? url.replace(regionRegex, '-' + region)
        : url.replace(regionRegex, '');
};
export const generateRegionForcedStandardUrl = (region, subDomain, env) => `https://${subDomain}.info360${region === 'eu' || region === 'ap' ? '-' + region : ''}-${env === 'prod' ? 'prd' : env}.com/${env}`;
export const STANDARD_LOCAL_URL = 'http://localhost:3276/dev';
const serviceMap = () => ({
    [Environments.LOCAL]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'dev'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'dev'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'dev'),
        assets: generateStandardUrl('saas-asset-management', 'dev'),
        assetsV2: generateStandardUrl('saas-asset-management', 'dev').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'dev'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'dev'),
        entitlements: generateStandardUrl('saas-auth', 'dev'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'dev'),
        iliManagement: generateStandardUrl('saas-ili-management', 'dev'),
        incident: generateStandardUrl('saas-incident-management', 'dev'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-dev.com/dev/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'dev'),
        massBalance: generateStandardUrl('saas-mass-balance', 'dev'),
        rules: generateStandardUrl('saas-rule-management', 'dev'),
        sensors: generateStandardUrl('saas-sensor', 'dev'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-dev.com/dev/'),
        workspaces: generateStandardUrl('saas-workspace', 'dev'),
        tiles: generateStandardUrl('tiles-dev', 'dev'),
        amUpload: generateStandardUrl('saas-upload-service', 'dev'),
        amInspection: generateStandardUrl('saas-inspection-management', 'dev'),
        superAdmin: generateStandardUrl('saas-superadmin', 'dev'),
        amRisk: generateStandardUrl('saas-risk-management', 'dev'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-dev.com/dev'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'dev'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'dev'),
        liftStations: generateStandardUrl('saas-lift-stations', 'dev'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'dev'),
        patterns: generateStandardUrl('saas-pattern', 'dev'),
        amRehab: generateStandardUrl('saas-rehab-management', 'dev'),
        export: generateStandardUrl('saas-export-service', 'dev'),
        imageManagement: generateStandardUrl('saas-image-management', 'dev'),
        idp: generateStandardUrl('saas-idp-router', 'dev'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'dev'),
        mapManagement: generateStandardUrl('saas-map-management', 'dev'),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'dev')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'dev'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'dev'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'dev'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'dev'),
        favorites: generateStandardUrl('saas-favorites', 'dev'),
        userManagement: generateStandardUrl('saas-user-management', 'dev'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-dev-dev.info360-dev.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-dev-dev-appsync.saas-notification-hub.wlc360p-c-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'dev'),
        goals: generateStandardUrl('saas-goal-management', 'dev'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-dev.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'dev'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'dev'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'dev'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'dev'),
    },
    [Environments.DEV]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'dev'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'dev'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'dev'),
        assets: generateStandardUrl('saas-asset-management', 'dev'),
        assetsV2: generateStandardUrl('saas-asset-management', 'dev').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'dev'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'dev'),
        entitlements: generateStandardUrl('saas-auth', 'dev'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'dev'),
        iliManagement: generateStandardUrl('saas-ili-management', 'dev'),
        incident: generateStandardUrl('saas-incident-management', 'dev'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-dev.com/dev/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'dev'),
        massBalance: generateStandardUrl('saas-mass-balance', 'dev'),
        rules: generateStandardUrl('saas-rule-management', 'dev'),
        sensors: generateStandardUrl('saas-sensor', 'dev'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-dev.com/dev/'),
        workspaces: generateStandardUrl('saas-workspace', 'dev'),
        tiles: generateStandardUrl('tiles-dev', 'dev'),
        amUpload: generateStandardUrl('saas-upload-service', 'dev'),
        amInspection: generateStandardUrl('saas-inspection-management', 'dev'),
        superAdmin: generateStandardUrl('saas-superadmin', 'dev'),
        amRisk: generateStandardUrl('saas-risk-management', 'dev'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-dev.com/dev'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-dev.info360|region|-dev.com/dev'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'dev'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'dev'),
        liftStations: generateStandardUrl('saas-lift-stations', 'dev'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'dev'),
        patterns: generateStandardUrl('saas-pattern', 'dev'),
        amRehab: generateStandardUrl('saas-rehab-management', 'dev'),
        export: generateStandardUrl('saas-export-service', 'dev'),
        imageManagement: generateStandardUrl('saas-image-management', 'dev'),
        idp: generateStandardUrl('saas-idp-router', 'dev'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'dev'),
        mapManagement: generateStandardUrl('saas-map-management', 'dev'),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'dev')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'dev'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'dev'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'dev'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'dev'),
        favorites: generateStandardUrl('saas-favorites', 'dev'),
        userManagement: generateStandardUrl('saas-user-management', 'dev'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-dev-dev.info360-dev.com/graphql',
        notificationHubPlatform: 'https://saas-notification-hub-dev-dev-appsync.saas-notification-hub.wlc360p-c-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'dev'),
        goals: generateStandardUrl('saas-goal-management', 'dev'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-dev.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'dev'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'dev'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'dev'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'dev'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'dev'),
    },
    [Environments.QA]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'qa'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'qa'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'qa'),
        assets: generateStandardUrl('saas-asset-management', 'qa'),
        assetsV2: generateStandardUrl('saas-asset-management', 'qa').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'qa'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'qa'),
        entitlements: generateStandardUrl('saas-auth', 'qa'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'qa'),
        iliManagement: generateStandardUrl('saas-ili-management', 'qa'),
        incident: generateStandardUrl('saas-incident-management', 'qa'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-qa.com/qa/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'qa'),
        massBalance: generateStandardUrl('saas-mass-balance', 'qa'),
        rules: generateStandardUrl('saas-rule-management', 'qa'),
        sensors: generateStandardUrl('saas-sensor', 'qa'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-qa.com/qa/'),
        workspaces: generateStandardUrl('saas-workspace', 'qa'),
        tiles: generateStandardUrl('tiles', 'qa'),
        amUpload: generateStandardUrl('saas-upload-service', 'qa'),
        amInspection: generateStandardUrl('saas-inspection-management', 'qa'),
        superAdmin: generateStandardUrl('saas-superadmin', 'qa'),
        amRisk: generateStandardUrl('saas-risk-management', 'qa'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-qa.com/qa'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'qa'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'qa'),
        liftStations: generateStandardUrl('saas-lift-stations', 'qa'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'qa'),
        // autodeskUser: generateStandardUrl('saas-auth', 'qa'),
        patterns: generateStandardUrl('saas-pattern', 'qa'),
        amRehab: generateStandardUrl('saas-rehab-management', 'qa'),
        export: generateStandardUrl('saas-export-service', 'qa'),
        imageManagement: generateStandardUrl('saas-image-management', 'qa'),
        idp: generateStandardUrl('saas-idp-router', 'qa'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'qa'),
        mapManagement: generateStandardUrl('saas-map-management', 'qa'),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'qa')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'qa'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'qa'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'qa'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'qa'),
        favorites: generateStandardUrl('saas-favorites', 'qa'),
        userManagement: generateStandardUrl('saas-user-management', 'qa'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-qa-qa.info360-qa.com/graphql',
        notificationHubPlatform: 'http://saas-notification-hub-stg-stg-appsync.saas-notification-hub.wlc360p-s-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'qa'),
        goals: generateStandardUrl('saas-goal-management', 'qa'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-qa.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'qa'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'qa'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'qa'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'qa'),
    },
    [Environments.STAGE]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'qa'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'qa'),
        analyticFunctions: generateStandardUrl('saas-analytic-functions', 'qa'),
        assets: generateStandardUrl('saas-asset-management', 'qa'),
        assetsV2: generateStandardUrl('saas-asset-management', 'qa').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'qa'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'qa'),
        entitlements: generateStandardUrl('saas-auth', 'qa'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'qa'),
        iliManagement: generateStandardUrl('saas-ili-management', 'qa'),
        incident: generateStandardUrl('saas-incident-management', 'qa'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-qa.com/qa/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'qa'),
        massBalance: generateStandardUrl('saas-mass-balance', 'qa'),
        rules: generateStandardUrl('saas-rule-management', 'qa'),
        sensors: generateStandardUrl('saas-sensor', 'qa'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-qa.com/qa/'),
        workspaces: generateStandardUrl('saas-workspace', 'qa'),
        tiles: generateStandardUrl('tiles', 'qa'),
        amUpload: generateStandardUrl('saas-upload-service', 'qa'),
        amInspection: generateStandardUrl('saas-inspection-management', 'qa'),
        superAdmin: generateStandardUrl('saas-superadmin', 'qa'),
        amRisk: generateStandardUrl('saas-risk-management', 'qa'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-qa.com/qa'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data-qa.info360|region|-qa.com/qa'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'qa'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'qa'),
        liftStations: generateStandardUrl('saas-lift-stations', 'qa'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'qa'),
        // autodeskUser: generateStandardUrl('saas-auth', 'qa'),
        patterns: generateStandardUrl('saas-pattern', 'qa'),
        amRehab: generateStandardUrl('saas-rehab-management', 'qa'),
        export: generateStandardUrl('saas-export-service', 'qa'),
        imageManagement: generateStandardUrl('saas-image-management', 'qa'),
        idp: generateStandardUrl('saas-idp-router', 'qa'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'qa'),
        mapManagement: generateStandardUrl('saas-map-management', 'qa'),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'qa')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'qa'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'qa'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'qa'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'qa'),
        favorites: generateStandardUrl('saas-favorites', 'qa'),
        userManagement: generateStandardUrl('saas-user-management', 'qa'),
        forgeApi: 'https://api.stg-manage.autodesk.com',
        notificationHub: 'https://saas-notification-hub-appsync-qa-qa.info360-qa.com/graphql',
        notificationHubPlatform: 'http://saas-notification-hub-stg-stg-appsync.saas-notification-hub.wlc360p-s-uw2.autodesk.com/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'qa'),
        goals: generateStandardUrl('saas-goal-management', 'qa'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-qa.com'),
        semantic: generateStandardUrl('saas-emagin-semantic', 'qa'),
        optimizationApi: generateStandardUrl('saas-emagin-optimization-api', 'qa'),
        modelFactory: generateStandardUrl('saas-emagin-model-factory', 'qa'),
        workgroupDatastore: generateStandardUrl('saas-workgroup-datastore', 'qa'),
        simulationResults: generateStandardUrl('saas-simulation-results', 'qa'),
    },
    [Environments.PROD]: {
        alertsManager: generateStandardUrl('saas-alerts-management', 'prod'),
        alertsScheduler: generateStandardUrl('saas-alerts-scheduler', 'prod'),
        analyticFunctions: 'https://saas-analytic-functions.info360-prd.com/prod',
        assets: generateStandardUrl('saas-asset-management', 'prod'),
        assetsV2: generateStandardUrl('saas-asset-management', 'prod').concat('/v2'),
        billing: generateStandardUrl('saas-billing', 'prod'),
        edgeManager: generateStandardUrl('saas-edge-manager', 'prod'),
        entitlements: generateStandardUrl('saas-auth', 'prod'),
        iliEngine: generateStandardUrl('saas-ili-engine', 'prod'),
        iliManagement: generateStandardUrl('saas-ili-management', 'prod'),
        incident: generateStandardUrl('saas-incident-management', 'prod'),
        looker: generateDeviantUrl('https://saas-looker.info360|region|-prd.com/prod/looker'),
        lookerAlerts: generateStandardUrl('saas-looker-alerts', 'prod'),
        massBalance: generateStandardUrl('saas-mass-balance', 'prod'),
        rules: generateStandardUrl('saas-rule-management', 'prod'),
        sensors: generateStandardUrl('saas-sensor', 'prod'),
        simulation: generateDeviantUrl('https://saas-simulation.info360|region|-prd.com/prod/'),
        workspaces: generateStandardUrl('saas-workspace', 'prod'),
        tiles: generateStandardUrl('tiles', 'prod'),
        amUpload: generateStandardUrl('saas-upload-service', 'prod'),
        amInspection: generateStandardUrl('saas-inspection-management', 'prod'),
        superAdmin: generateStandardUrl('saas-superadmin', 'prod'),
        amRisk: generateStandardUrl('saas-risk-management', 'prod'),
        amRiskWss: generateDeviantUrl('wss://saas-asset-web-socket.info360|region|-prd.com/prod'),
        dataAuthorizer: generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com/prod'),
        sensorData: generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com'),
        sensorDataV2: generateDeviantUrl('https://saas-info360-sensor-data.info360|region|-prd.com/prod'),
        valueMapping: generateStandardUrl('saas-value-mapping', 'prod'),
        sensorGroup: generateStandardUrl('saas-sensor-group', 'prod'),
        liftStations: generateStandardUrl('saas-lift-stations', 'prod'),
        customAnalytics: generateStandardUrl('saas-custom-analytics', 'prod'),
        // autodeskUser: generateStandardUrl('saas-auth', 'prod'),
        patterns: generateStandardUrl('saas-pattern', 'prod'),
        amRehab: generateStandardUrl('saas-rehab-management', 'prod'),
        export: generateStandardUrl('saas-export-service', 'prod'),
        imageManagement: generateStandardUrl('saas-image-management', 'prod'),
        idp: generateStandardUrl('saas-idp-router', 'prod'),
        serviceIntegration: generateStandardUrl('saas-serviceintegration', 'prod'),
        mapManagement: generateStandardUrl('saas-map-management', 'prod'),
        emaginOptimization: generateStandardUrl('saas-emagin-optimization-api', 'prod'),
        emaginHarvester: removePathPartFromUrl(generateStandardUrl('saas-emagin-harvester', 'prod')),
        emaginMoments: generateStandardUrl('saas-emagin-moments', 'prod'),
        sharedTemplates: generateStandardUrl('saas-shared-templates', 'prod'),
        sharedTemplatesProxy: generateStandardUrl('saas-shared-templates', 'prd'),
        emaginIntegeration: generateStandardUrl('saas-info360-emagin-integ', 'prod'),
        favorites: generateStandardUrl('saas-favorites', 'prod'),
        userManagement: generateStandardUrl('saas-user-management', 'prod'),
        forgeApi: 'https://api.manage.autodesk.com',
        notificationHub: generateNotificationHubAppsyncUrl('saas-notification-hub-appsync-prod-prod'),
        notificationHubPlatform: generatePlatformUrl('saas-notification-hub-prd-prd-appsync.saas-notification-hub', Environments.PROD) + '/graphql',
        edgeAnalytics: generateStandardUrl('saas-edge-analytics', 'prod'),
        goals: generateStandardUrl('saas-goal-management', 'prod'),
        flowGPT: generateDeviantUrl('https://saas-emagin-flowgpt.info360|region|-prd.com'),
        semantic: generateDeviantUrl('https://saas-emagin-semantic.info360|region|-prd.com/prod'),
        optimizationApi: generateDeviantUrl('https://saas-emagin-optimization-api.info360|region|-prd.com/prod'),
        modelFactory: generateDeviantUrl('https://saas-emagin-model-factory.info360|region|-prd.com/prod'),
        workgroupDatastore: generateDeviantUrl('https://saas-workgroup-datastore.info360|region|-prd.com/prod'),
        simulationResults: generateDeviantUrl('https://saas-simulation-results.info360|region|-prd.com/prod'),
    },
});
export const getService = (service, mapOverride = {}, environmentOverride) => {
    const serviceMapClone = cloneDeep(serviceMap());
    const map = merge(serviceMapClone, mapOverride);
    const env = isEnvironment(environmentOverride)
        ? environmentOverride
        : getEnvironment();
    return map[env][service];
};
